import React from 'react';
import PropTypes from 'prop-types';
import ButtonDropdown from 'src/components/buttons/buttonDropdown/buttonDropdown';
import config from 'config/config';
import { connect } from 'react-redux';
import { fetchPrivatePost } from 'src/modules/community/singlePost/actions/fetchActions';
import { pollFetch } from 'src/pages/community/poll/actions';
import { lockPost, unlockPost, togglePostPin, restrictUser } from 'src/modules/community/singlePost/actions/actions';
import { fetchCurrentCommunityRoles } from 'src/modules/shared/context/actions';
import { showModal } from 'src/modules/shared/modal/actions';
import { setAlert } from 'src/typescript/components/alert/actions';
import { navigateTo } from 'src/utils/router';
import { userHasPermission, userHasRole } from 'src/utils/context';
import i18n from 'src/utils/translate';
import { DropdownItem } from 'reactstrap';
import { useHelpScout } from 'src/utils/hooks';
import { LinkButton } from 'styles-js/buttons';
// TODO: Remove assign badges
import { assignBadges, removeBadges } from 'src/modules/community/singlePost/actions/actions';

class ActionsDropdown extends React.Component {
  static propTypes = {
    canPin: PropTypes.bool,
    community: PropTypes.object,
    dataTestId: PropTypes.string,
    post: PropTypes.object,
    referer: PropTypes.any,
    roles: PropTypes.array,
    superPost: PropTypes.object,
    onEdit: PropTypes.func,
    onLockChanged: PropTypes.func,
  };

  static defaultProps = {
    canPin: true,
    dataTestId: null,
    onEdit: () => {},
    onLockChanged: () => {},
    size: 'md',
    superPost: {},
  };

  onSelect = async (value) => {
    const { post, community, superPost, showModal, setAlert } = this.props;
    const isPoll = superPost.postTypeId === config.api.postTypes.POLL;

    switch (value) {
      case 'edit':
        if (post.id === superPost.id) {
          const postType = post.postTypeId === config.api.postTypes.POLL ? 'polls' : 'posts';
          navigateTo(`/${community.slug}/${postType}/edit/${post.id}`);
        }
        this.props.onEdit();
        break;

      case 'delete':
        showModal({
          modalType: 'DELETE_POST',
          modalProps: { post, superPost },
        });
        break;

      // TODO: Remove assign badges
      case 'assign':
        await this.props.fetchCurrentCommunityRoles(community, false);
        showModal({
          modalType: 'ASSIGN_BADGES',
          modalProps: {
            assignBadges: this.props.assignBadges,
            author: this.props.post.author,
            communitySlug: community.slug,
            removeBadges: this.props.removeBadges,
            setAlert: setAlert,
            onClose: () => {
              if (isPoll) {
                this.props.pollFetch(this.props.superPost.id, community.slug);
              } else {
                this.props.fetchPrivatePost(this.props.superPost.id, community.slug);
              }
            },
          },
        });
        break;

      case 'edit-badges':
        showModal({
          modalType: 'EDIT_BADGES',
          modalProps: {
            member: this.props.post.author,
          },
        });
        break;

      case 'restrict':
        const restrictSuccessMessage = i18n.t('User %1$s has been restricted').replace('%1$s', post.author.username);
        const alreadyRestrictedMessage = i18n.t('This user is already restricted. You can unrestrict from the admin section.');
        showModal({
          modalProps: {
            title: i18n.t('Restrict user'),
            cancel: i18n.t('Cancel'),
            accept: i18n.t('Restrict'),
            onAccept: () => this.props.restrictUser(post.author, community.slug, restrictSuccessMessage, alreadyRestrictedMessage),
            children: i18n.t('Restricted users cannot create posts or respond to posts in this community, or send private messages to members of this community.'),
          },
        });
        break;

      case 'pin':
        this.props.togglePostPin(post.id, community.slug);
        break;

      case 'lock':
        showModal({
          modalProps: {
            title: i18n.t('Are you sure you want to do this?'),
            cancel: i18n.t('Cancel'),
            accept: i18n.t('Turn off replying'),
            onAccept: async () => {
              await this.props.lockPost(post.id, community.slug);
              setAlert(i18n.t('You turned off replying for this post.'), 'success');
            },
            children: (
              <p>
                {i18n.t('This will prevent users from creating or editing replies and the author of the post will be notified that replying has been disabled.')}<br />
                <LinkButton onClick={() => useHelpScout('36-turn-off-replying-on-posts')}>{i18n.t('Learn more')}</LinkButton>
              </p>
            ),
          }});
        break;

      case 'unlock':
        showModal({
          modalProps: {
            children: i18n.t('This will allow users to create and edit replies.'),
            title: i18n.t('Are you sure you want to do this?'),
            cancel: i18n.t('Cancel'),
            accept: i18n.t('Yes'),
            onAccept: async () => {
              await this.props.unlockPost(post.id, community.slug);
              setAlert(i18n.t('You turned on replying for this post.'), 'success');
            },
          },
        });
        break;

      case 'sentiment':
        showModal({ modalType: 'SENTIMENT_FEEDBACK' });
    }
  }

  render() {
    const { post, currentUser, dataTestId } = this.props;

    if (!post || !currentUser) return null;

    const { author } = post;

    const isModeratorRole = userHasRole(currentUser, 'Moderator');

    const threadPost = this.props.superPost || post;
    const isPoll = threadPost.postTypeId === config.api.postTypes.POLL;

    const canEdit = (author.id === currentUser.userId ||
      (!isPoll && userHasPermission(currentUser, 'EditPost')) ||
      (isPoll && userHasPermission(currentUser, 'EditPoll'))) && !threadPost.isLocked;

    const canDelete = (author.id === currentUser.userId ||
      (!isPoll && userHasPermission(currentUser, 'DeletePost')) ||
      (isPoll && userHasPermission(currentUser, 'DeletePoll')));

    const canTurnOffReplying = userHasPermission(currentUser, 'LockPost')
      && (!this.props.superPost.id || (this.props.superPost.id === post.id));

    // TODO: assign badges
    const canAssignBadges = (!isPoll || (isPoll && !isModeratorRole)) && userHasPermission(currentUser, 'CreateUserRole')
      && author.id !== currentUser.userId;
    const canEditBadges = userHasRole(currentUser, ['HealthUnlocked']); //, 'Partner', 'Administrator', 'Moderator']); // only rolling out to HU staff for now

    const canRestrict = (!isPoll || (isPoll && !isModeratorRole)) && userHasPermission(currentUser, 'CreateUserRole')
      && author.id !== currentUser.userId;

    const canPin = (!isPoll || (isPoll && !isModeratorRole)) && userHasPermission(currentUser, 'PinPost') && this.props.canPin;
    const canGiveSentimentFeedback = !!currentUser && this.props.superPost.id === post.id;

    const actions = [
      canEdit && { label: i18n.t('Edit'), value: 'edit' },
      canDelete && { label: i18n.t('Delete'), value: 'delete' },
      canTurnOffReplying && !post.isLocked && { label: i18n.t('Turn off replying'), value: 'lock' },
      canTurnOffReplying && !!post.isLocked && { label: i18n.t('Turn on replying'), value: 'unlock' },
      // TODO: remove assign badges
      canAssignBadges && { label: i18n.t('Assign badges'), value: 'assign' },
      canEditBadges && { label: i18n.t('Edit badges'), value: 'edit-badges' },
      canRestrict && { label: i18n.t('Restrict user'), value: 'restrict' },
      canPin && { label: post.isPinned ? i18n.t('Unpin') : i18n.t('Pin'), value: 'pin' },
      canGiveSentimentFeedback && { label: i18n.t('Sentiment feedback'), value: 'sentiment' },
    ].filter(x => !!x);

    if (actions.length === 0) return null;

    return (
      <div
        key={`actions${post.id}`}
        style={{display: 'inline-block'}}
        title={i18n.t('Interact with posts')}
      >
        <ButtonDropdown
          color={this.props.color}
          dataTestId={dataTestId}
          showActive={false}
          size={this.props.size}
          text={this.props.buttonText}
        >
          {actions.map(action => (
            <DropdownItem
              data-test-id={dataTestId && `${dataTestId}__${action.value}-button`}
              key={action.value}
              onClick={() => this.onSelect(action.value)}
            >
              {action.label}
            </DropdownItem>
          ))}
        </ButtonDropdown>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    community: state.context.currentCommunity,
    currentUser: state.context.currentUser,
  };
};

const actionProps = {
  lockPost,
  unlockPost,
  togglePostPin,
  fetchPrivatePost,
  fetchCurrentCommunityRoles,
  restrictUser,
  showModal,
  setAlert,
  pollFetch,
  // TODO: Remove assign badges
  assignBadges,
  removeBadges,
};

export default connect(mapStateToProps, actionProps)(ActionsDropdown);
